import { Dispatch, SetStateAction, useEffect } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { StudentsSkillProgressReportQuery } from '../../graphQLOperations/__generated__/StudentsSkillProgressReportQuery.graphql';
import { studentSkillProgressReportQuery } from '../../graphQLOperations/Students';
import { SkillResult } from '../../models/Skill';
import { useAppSelector } from '../../redux/hooks';
import { selectedReportsFilter } from '../../redux/reducers/filterReportsSlice';
import { getAllTargetSkillTypes } from '../../graphQLOperations/Skills';
import { SkillsAllTargetSkillTypesQuery } from '../../graphQLOperations/__generated__/SkillsAllTargetSkillTypesQuery.graphql';

export interface ProgressTableStudentsFilterState {
  first: number;
  rows: number;
  page?: number;
  pageCount: number;
  totalPages: number;
  prevPage: number;
  filters: {
    studentName: string;
  };
}

export interface ProgressTableStudentsValue {
  studentFirstName: string;
  studentLastName: string;
  studentId: string;
  studentPublicUserId: string;
  studentSkillsResults: { key: SkillResult; value: number }[];
  roadMapPercentage: number | null;
  lastLogin: string | null;
  currentRoadmap: string;
  currentRoadmapName: string;
}

interface Props {
  setFetchingData: Dispatch<SetStateAction<boolean>>;
  fetchingData: boolean;
  firstLoad: boolean;
  setFirstLoad: Dispatch<SetStateAction<boolean>>;
  fetchKey: number;
  setFetchKey: Dispatch<SetStateAction<number>>;
  setValues: Dispatch<SetStateAction<ProgressTableStudentsValue[]>>;
}

const QueryStudentsProgressReport = ({
  fetchKey,
  fetchingData,
  firstLoad,
  setFetchKey,
  setFetchingData,
  setFirstLoad,
  setValues,
}: Props) => {
  const reportsFilterRedux = useAppSelector(selectedReportsFilter);

  const { allTargetSkillTypes } = useLazyLoadQuery<SkillsAllTargetSkillTypesQuery>(
    getAllTargetSkillTypes,
    {},
    { fetchPolicy: 'network-only' },
  );

  const { studentSkillProgressReport } = useLazyLoadQuery<StudentsSkillProgressReportQuery>(
    studentSkillProgressReportQuery,
    {
      skip: !firstLoad && !fetchingData,
      filter: {
        classIds: reportsFilterRedux?.classes?.map(cl => cl?.id),
        targetGroupIds: reportsFilterRedux?.roadmaps?.id ? [reportsFilterRedux?.roadmaps?.id] : [],
        skillTypeIds: allTargetSkillTypes?.filter(type => type?.name !== 'Extension')?.map(type => type?.id),
      },
    },
    { fetchKey: fetchKey, fetchPolicy: 'network-only' },
  );

  useEffect(() => {
    setFetchKey(prev => prev + 1);
    setFetchingData(true);
  }, [reportsFilterRedux?.sites, reportsFilterRedux?.roadmaps?.id, reportsFilterRedux?.classes?.length]);

  useEffect(() => {
    if (studentSkillProgressReport && fetchingData) {
      setFetchingData(false);
      setFirstLoad(false);
      setValues(
        studentSkillProgressReport?.map(
          val =>
            ({
              studentId: val?.student?.id ?? '',
              studentLastName: val?.student?.lastName || '',
              studentFirstName: val?.student?.firstName || '',
              studentPublicUserId: val?.student?.publicUserId || '',
              studentSkillsResults: val?.skillIdsBySkillResults ?? [],
              lastLogin: val.lastLogin ?? '',
              roadMapPercentage: val.roadmapPercentage ?? 0,
              currentRoadmap: val.currentRoadmapId != null ? String(val.currentRoadmapId) : null,
              currentRoadmapName: val.currentRoadmapName != null ? String(val.currentRoadmapName) : null,
            }) as ProgressTableStudentsValue,
        ),
      );
    }
    setFetchingData(false);
  }, [studentSkillProgressReport, fetchingData]);
  return <div style={{ display: 'none' }}></div>;
};

export default QueryStudentsProgressReport;
